// @flow
import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withLeaflet } from 'react-leaflet';
import type { LeafletContext } from 'react-leaflet/lib/types';
import { History, withRouter } from 'react-router-dom';
import { get, isEmpty } from 'lodash';
import { Formik } from 'formik';
import memoizeOne from 'memoize-one';
import * as sidePanelActionCreators from 'esm-web-pro/redux/side-panel/actions';
import BaseEditor, { DRAW_FG } from 'esm-web-pro/containers/map/editor/BaseEditor';
import { ApiFormWithModel } from 'esm-web-pro/containers/forms/FormWithModel';
import {
    COORDINATES_EDIT_ALLOWED_DIGITS,
    LAT_LNG_MAX_MARGIN,
    reverseTransformPointCoords,
    transformPointCoords,
} from 'esm-web-pro/containers/pages/network/Util';
import { getCoordinateSystem } from 'esm-web-pro/redux/networks';
import { ApiClient } from 'speed-js-core';
import connectAPI from 'speed-js-react/src/api/connectAPI';
import type { UUID } from 'esm-web-pro/types/common';

type Props = {
    id: string,
    point: Object,
    objectLayer: Object,
    onClose: Function,
    onSave: Function,
    resource: string,
    // eslint-disable-next-line react/no-unused-prop-types
    history: Object,
    match: Object,
    updateObject: Function,
    // eslint-disable-next-line react/no-unused-prop-types
    editor: BaseEditor,
    leaflet: any | LeafletContext,
    history: History,
    setSensitivity: (key: string, sensitivity: boolean) => void,
    onView: Function,
    coordinateSystem: string,
    getConnectedLines: Function,
    getInfrastructureProvider: (uuid: UUID) => Promise<Object>,
    getProjectStages: (uuid: UUID) => Promise<Object>,
    mapIdentity: string,
};

type State = {
    excludeFields: Array<string>,
    stageChoices: Object,
    lastSelectedProject: string | null,
    provider: Object,
};


@withRouter
@withLeaflet
@connect((state) => ({ coordinateSystem: getCoordinateSystem(state) }),
    (dispatch) => (bindActionCreators(sidePanelActionCreators, dispatch)))
@connectAPI((apiClient: ApiClient) => ({
    getConnectedLines: {
        send: (id: UUID) => apiClient.resources.point11.connectedLines(id),
    },
    getInfrastructureProvider: {
        send: (uuid: UUID) => apiClient.resources.infrastructureProvider.get(uuid),
    },
    getProjectStages: {
        send: (uuid: UUID) => apiClient.resources.projectsStageNested.list(uuid),
    },
}))
class Point11Editor extends React.Component<Props, State> {
    formRef: { current: null | React.ElementRef<Formik> };

    resetLayer: boolean;

    static defaultProps = {
        leaflet: undefined,
    };

    connectedLines: Array<Object> = [];

    getInfrastructureProvider: (uuid: UUID) => Object;

    constructor(props: Props) {
        super(props);

        const { getInfrastructureProvider, point, coordinateSystem } = props;

        this.formRef = React.createRef();
        this.resetLayer = true;
        this.objectLayerGroup = null;

        const pointInitial = { ...point };

        const initial = {
            ...pointInitial,
            coordinates: {
                ...point.coordinates,
                coordinates:
                    coordinateSystem === process.env.REACT_APP_GIS_TRANSFORM_COORDINATE_SYSTEM
                        ? transformPointCoords(point.coordinates.coordinates) : point.coordinates.coordinates,
            },
            infrastructure_provider: point.infrastructure_provider.uuid,
            property_owner: point?.property_owner?.uuid,
            project: point.project?.uuid,
            project_stage: point.project_stage?.uuid,
        };

        this.state = {
            excludeFields: this.getInitialExcludeFields(),
            provider: null,
            lastSelectedProject: null,
            stageChoices: {},
            initial,
        };


        this.getInfrastructureProvider = memoizeOne(getInfrastructureProvider);
    }

    componentDidMount(): void {
        const {
            editor, onView, objectLayer, point,
        } = this.props;
        onView();
        this.findConnectedLines();
        editor.registerLoadingCallback(this.getNewConnectedLinesWhenLoading);
        editor.focus([objectLayer, ...this.connectedLines]);
        editor.reloadFeatures();

        // Add to draw feature group because of zoom restrictions
        this.objectLayerGroup = objectLayer.pm._layerGroup;
        this.objectLayerGroup.removeLayer(objectLayer);
        // prevent adding duplicate leaflet element if the drawing feature group already has it
        if (!editor.featureGroups[DRAW_FG].current.leafletElement.hasLayer(objectLayer)) {
            editor.featureGroups[DRAW_FG].current.leafletElement.addLayer(objectLayer);
        }

        if (point.infrastructure_provider) {
            this.setState({ provider: { ...point.infrastructure_provider } });
            this.getInfrastructureProvider(point.infrastructure_provider.uuid).then((providerResp) => {
                if (providerResp) {
                    this.setState({ provider: providerResp });
                    if (providerResp.is_property_owner) {
                        this.excludeProviderFields(providerResp);
                    }
                }
            });
        }

        if (point.project?.uuid) {
            this.getStageChoices(point.project.uuid).then((projectStageChoices) => {
                this.setState({ stageChoices: projectStageChoices });
            });
        }
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const {
            point, objectLayer, coordinateSystem,
        } = this.props;

        this.enableEditMode();

        if (coordinateSystem !== prevProps.coordinateSystem && objectLayer && this.formRef.current) {
            const coords = objectLayer.getLatLng();

            const newCoords = coordinateSystem === process.env.REACT_APP_GIS_TRANSFORM_COORDINATE_SYSTEM
                ? transformPointCoords([coords.lng, coords.lat]) : [coords.lng, coords.lat];

            this.formRef.current.getFormikBag().setFieldValue('coordinates', {
                ...point.coordinates,
                coordinates: newCoords,
            });
        }
        this.checkIfProviderChanged(prevState.provider);
    }

    componentWillUnmount() {
        const { editor, objectLayer } = this.props;
        this.handleCloseEdit();
        objectLayer.pm.disable(); // TODO: Check why sometimes the layer is null
        editor.unregisterLoadingCallback(this.getNewConnectedLinesWhenLoading);
        // Clear the focus from the point and its lines so that they can
        // be removed from the map if they go out of the viewport
        editor.focus();
        if (editor.featureGroups[DRAW_FG].current) {
            editor.featureGroups[DRAW_FG].current.leafletElement.removeLayer(objectLayer);
        }
        if (this.objectLayerGroup) {
            objectLayer.addTo(this.objectLayerGroup);
        }
        if (this.connectedLines) {
            const { connectedLines } = this;
            connectedLines.forEach((connectedLine) => {
                connectedLine.pointMovedIndex = -1;
            });
        }
    }

    enableEditMode = () => {
        const { objectLayer } = this.props;

        const leafletPmEnabled = objectLayer.pm.enabled();
        // Make sure the form is not submitting or validating before enabling the edit mode
        if (
            objectLayer
            && !leafletPmEnabled
            && this.formRef.current
        ) {
            objectLayer.pm.enable();
        }
    };

    getInitialExcludeFields = () => {
        const { point } = this.props;
        if (point.project_stage?.uuid) {
            return ['stage', 'ref'];
        }
        return ['stage', 'ref', 'project_stage'];
    };

    getStageChoices = (project: UUID): Object => {
        const { getProjectStages } = this.props;
        return getProjectStages(project).then((res) => {
            const projectStageChoices = {};
            res.results.forEach((stage) => {
                projectStageChoices[stage.uuid] = stage.name;
            });
            return projectStageChoices;
        });
    }

    checkIfProviderChanged = (prevProvider) => {
        const { provider } = this.state;

        if (prevProvider?.uuid !== provider?.uuid) {
            this.excludeProviderFields(provider, prevProvider);
        }
    };

    excludeProviderFields = (provider, prevProvider = null) => {
        if (!provider) return;
        const { point } = this.props;
        const { excludeFields: prevExcludeFields } = this.state;

        const excludeFields = [...prevExcludeFields];
        let excludeFieldsChanged = false;
        const fields = {};

        const exportIndex = excludeFields.indexOf('export');
        if (provider.entity?.name !== process.env.REACT_APP_DEFAULT_PROVIDER) {
            if (exportIndex === -1) {
                excludeFieldsChanged = true;
                excludeFields.push('export');
            }
            fields.export = false;
        } else if (prevProvider?.entity?.name !== process.env.REACT_APP_DEFAULT_PROVIDER) {
            excludeFieldsChanged = true;
            if (exportIndex > -1) {
                excludeFields.splice(exportIndex, 1);
            }
            fields.export = point.export;
        }

        const propertyOwnerIndex = excludeFields.indexOf('property_owner');
        if (!provider.is_property_owner) {
            if (propertyOwnerIndex === -1) {
                excludeFieldsChanged = true;
                excludeFields.push('property_owner');
            }
            fields.property_owner = null;
        } else {
            excludeFieldsChanged = true;
            if (propertyOwnerIndex > -1) {
                excludeFields.splice(propertyOwnerIndex, 1);
            }

            if (point?.property_owner?.uuid) {
                const propertyOwnerUuid = point.property_owner.uuid;
                fields.property_owner = propertyOwnerUuid;
            }
        }

        if (!isEmpty(fields) && this.formRef.current) {
            Object.entries(fields).forEach(([field, value]) => {
                this.formRef.current.getFormikBag().setFieldValue(field, value);
            });
        }


        this.setState((prevState) => {
            const { initial: prevInitial } = prevState;
            const newState = {};

            if (excludeFieldsChanged) {
                newState.excludeFields = excludeFields;
            }

            if (!isEmpty(fields)) {
                newState.initial = { ...prevInitial, ...fields };
            }

            return newState;
        });
    };

    findConnectedLines = () => {
        const {
            leaflet: { map }, point, objectLayer, getConnectedLines,
        } = this.props;

        if (objectLayer) {
            objectLayer.on('drag', this.handleDrag);
        }

        const key = `${point.coordinates.coordinates[0]}-${point.coordinates.coordinates[1]}`;
        this.connectedLines = get(map, ['coordsToLayers', key, 'lines']) || [];

        if (!isEmpty(this.connectedLines)) {
            getConnectedLines(point.uuid).then((res) => {
                this.connectedLines = this.connectedLines.filter((l) => res.includes(l.feature.id));
                this.connectedLines.forEach((line) => {
                    const startCoords = line.getLatLngs()[0];
                    const endCoords = line.getLatLngs()[line.getLatLngs().length - 1];
                    if (startCoords.equals(objectLayer.getLatLng(), LAT_LNG_MAX_MARGIN)) {
                        line.pointMovedIndex = 0;
                    } else if (endCoords.equals(objectLayer.getLatLng(), LAT_LNG_MAX_MARGIN)) {
                        line.pointMovedIndex = line.getLatLngs().length - 1;
                    }
                });
            });
        }
    };

    returnToOriginal = () => {
        if (this.connectedLines) {
            const { connectedLines, props: { leaflet: { map } } } = this;
            connectedLines.forEach((connectedLine) => {
                connectedLine.setLatLngs(connectedLine.feature.geometry.coordinates.map((c) => [c[1], c[0]]));
                if (connectedLine.contentTooltip && map.hasLayer(connectedLine)) {
                    connectedLine.contentTooltip.setLatLng(connectedLine.getCenter());
                }
            });
        }
    };

    getNewConnectedLinesWhenLoading = (layer: Object) => {
        if (layer.feature) {
            const { leaflet: { map } } = this.props;
            const { connectedLines } = this;
            const index = connectedLines.findIndex((line) => line.feature.id === layer.feature.id);

            if (index !== -1) {
                layer.setLatLngs(connectedLines[index].getLatLngs());
                layer.pointMovedIndex = connectedLines[index].pointMovedIndex;
                connectedLines[index] = layer;
                if (layer.contentTooltip && map.hasLayer(layer)) {
                    layer.contentTooltip.setLatLng(layer.getCenter());
                }
            }
        }
    };

    handleCloseEdit = () => {
        const { point, objectLayer } = this.props;
        if (this.resetLayer) {
            objectLayer.setLatLng([point.coordinates.coordinates[1], point.coordinates.coordinates[0]]);
            this.returnToOriginal();
        }
    };

    handleDrag = (e) => {
        const { point, coordinateSystem } = this.props;

        this.moveLinesWithPoint(e.latlng.lat, e.latlng.lng);
        let coords = [Number(e.latlng.lng.toFixed(COORDINATES_EDIT_ALLOWED_DIGITS)),
            Number(e.latlng.lat.toFixed(COORDINATES_EDIT_ALLOWED_DIGITS))];

        if (coordinateSystem === process.env.REACT_APP_GIS_TRANSFORM_COORDINATE_SYSTEM) {
            coords = transformPointCoords(coords);
        }

        if (this.formRef.current) {
            this.formRef.current.getFormikBag().setFieldValue('coordinates', {
                ...point.coordinates,
                coordinates: coords,
            });
        }
    };

    moveLinesWithPoint = (lat: number, lng: number) => {
        const { leaflet: { map } } = this.props;
        this.connectedLines.forEach((line) => {
            const newCoords = line.getLatLngs();
            newCoords[line.pointMovedIndex] = {
                lat: Number(lat.toFixed(COORDINATES_EDIT_ALLOWED_DIGITS)),
                lng: Number(lng.toFixed(COORDINATES_EDIT_ALLOWED_DIGITS)),
            };
            line.setLatLngs(newCoords);
            if (line.contentTooltip && map.hasLayer(line)) {
                line.contentTooltip.setLatLng(line.getCenter());
            }
        });
    };

    handleFormMounted = () => {
        this.enableEditMode();
    };

    handleUpdate = (response: Object) => {
        const { onSave, setSensitivity, id } = this.props;
        const { match, history } = this.props;
        const urlUuid = match.params.objectIdentity;
        if (response.uuid !== urlUuid) {
            setSensitivity(id, false);
            history.push(match.url.replace(match.params.objectIdentity, response.identity));
        } else {
            onSave(response);
        }
    };

    handleSave = (response: Object) => {
        const { updateObject } = this.props;
        this.resetLayer = false;
        if (response.id) {
            updateObject(response);
        }

        this.connectedLines.forEach((line) => {
            const newCoords = line.getLatLngs();
            if (response.id) {
                newCoords[line.pointMovedIndex] = response.coordinates.coordinates.slice().reverse();
                line.setLatLngs(newCoords);
            }
        });
        if (response.id) {
            this.handleUpdate(response);
            this.handleClose();
        }
    };

    handleClose = () => {
        const { onClose } = this.props;
        onClose();
    };

    handleSubmit = (values: Object) => {
        const { objectLayer } = this.props;

        objectLayer.setLatLng(objectLayer.toGeoJSON().geometry.coordinates
            .map((c) => Number(c.toFixed(COORDINATES_EDIT_ALLOWED_DIGITS))).reverse());

        return {
            ...values,
            // TODO: Check why sometimes layer is null
            coordinates: {
                ...values.coordinates,
                coordinates: objectLayer.toGeoJSON().geometry.coordinates
                    .map((c) => Number(c.toFixed(COORDINATES_EDIT_ALLOWED_DIGITS))),
            },
        };
    };

    removeProjectStageFromExcludedFields = () => {
        const { excludeFields } = this.state;

        const newExcludedFields = excludeFields.filter((field) => field !== 'project_stage');

        this.setState({ excludeFields: newExcludedFields });
    }


    handleChange = async () => {
        if (this.formRef && this.formRef.current) {
            const { coordinateSystem, objectLayer } = this.props;

            await this.formRef.current.getFormikBag().validateForm();
            let { values: { coordinates } } = this.formRef.current.getFormikBag();

            if (coordinates.coordinates.some((v) => !v)) {
                return;
            }

            if (coordinateSystem === process.env.REACT_APP_GIS_TRANSFORM_COORDINATE_SYSTEM) {
                coordinates = {
                    ...coordinates,
                    coordinates: reverseTransformPointCoords(coordinates.coordinates),
                };
            }

            this.moveLinesWithPoint(coordinates.coordinates[1], coordinates.coordinates[0]);

            const newCoords = {
                lat: Number(coordinates.coordinates[1].toFixed(COORDINATES_EDIT_ALLOWED_DIGITS)),
                lng: Number(coordinates.coordinates[0].toFixed(COORDINATES_EDIT_ALLOWED_DIGITS)),
            };

            if (objectLayer) {
                objectLayer.setLatLng(newCoords);
            }
        }
    };

    handleValuesChanged = (values) => {
        const { provider, lastSelectedProject, excludeFields } = this.state;
        const { getProjectStages } = this.props;
        const { infrastructure_provider: infrastructureProvider, project } = values;

        if (infrastructureProvider !== provider) {
            this.getInfrastructureProvider(infrastructureProvider).then((res) => {
                if (res) {
                    this.setState({ provider: res });
                }
            });
        }
        if (project && project !== lastSelectedProject) {
            // get the possible stages based on the project, set them as project_stage field choices and remove it
            // from excluded fields
            this.setState({ lastSelectedProject: project });
            this.getStageChoices(project).then((projectStageChoices) => {
                if (projectStageChoices) {
                    this.removeProjectStageFromExcludedFields();
                    this.setState({ stageChoices: projectStageChoices });
                }
            });
        } else if (!project && !excludeFields.includes('project_stage')) {
            // if there is no project selected and project_stage is not in the excluded fields set it to null
            // and add it to excluded fields. Handles case when project and stage have been selected
            // but then the project field has been reset
            this.formRef.current.getFormikBag().setFieldValue('project_stage', null);
            const excludedFields = [...excludeFields];
            excludedFields.push('project_stage');
            this.setState({ excludeFields: excludedFields });
            this.setState({ lastSelectedProject: null });
        }
    };


    render() {
        const {
            point, resource, mapIdentity,
        } = this.props;
        const { excludeFields, initial, stageChoices } = this.state;

        return (
            <>
                <ApiFormWithModel
                    objectType={point.type}
                    forwardRef={this.formRef}
                    resourceName={resource}
                    objectId={point.uuid}
                    excludeFields={excludeFields}
                    extraSchema={[{ 'project_stage.ui.choices': stageChoices }]}
                    initial={initial}
                    onSave={this.handleSave}
                    onClose={this.handleClose}
                    onSubmit={this.handleSubmit}
                    height="100%"
                    partialUpdate
                    onChange={this.handleChange}
                    optionsResourceArguments={[{ map_identity: mapIdentity }]}
                    enableReinitialize
                    onValuesChanged={this.handleValuesChanged}
                    onFormMounted={this.handleFormMounted}
                />
            </>
        );
    }
}

export default Point11Editor;
